import { sortBy } from 'lodash';
import {
  Coordinate,
  CoordinateLike,
  CoordinateTuple,
} from '@shared/models/geography.model';
import { cacheFactory } from '../function.helpers';
import { getMedianValue, roundToDecimals } from '../math.helpers';
import { Point2D, PointLike } from '@shared/models/geometry.model';
import { toPoint2D } from './geometry.helpers';
import { guards } from '../typeguards.helpers';
import { LngLat } from 'mapbox-gl';

export type Line = [start: Point2D, end: Point2D];

export const getPointOnLine = (line: Line, ratio: number): Point2D => {
  const [p1, p2] = line;
  const x1 = p1[0];
  const y1 = p1[1];
  const x2 = p2[0];
  const y2 = p2[1];

  const x = x1 + ratio * (x2 - x1);
  const y = y1 + ratio * (y2 - y1);

  return [x, y];
};

const createPerpendicularPoint = (
  p1: Point2D,
  p2: Point2D,
  distance = 1,
): Point2D => [
  p2[0] +
    (distance * (p1[1] - p2[1])) /
      Math.sqrt(Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2)),
  p2[1] -
    (distance * (p1[0] - p2[0])) /
      Math.sqrt(Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2)),
];

export const createPerpendicularLine = (
  line: Line,
  length = 1,
  anchorPosition = 1,
): Line => {
  let p1: Point2D;
  let p2: Point2D;
  // Use start point as anchor
  if (anchorPosition === 0) {
    [p1, p2] = reverseLine(line);
  } else {
    p1 = line[0];
    p2 = getPointOnLine(line, anchorPosition);
  }

  const perp1 = createPerpendicularPoint(p1, p2, length / 2);
  const perp2 = createPerpendicularPoint(p1, p2, -length / 2);
  return [perp1, perp2];
};

export const findIntervalIntersection = (
  interval1: [number, number],
  interval2: [number, number],
): [number, number] | undefined => {
  const [start1, end1] = interval1;
  const [start2, end2] = interval2;

  const start = Math.max(start1, start2);
  const end = Math.min(end1, end2);

  if (start <= end) {
    return [start, end];
  }
};

/**
 * Get the distance between two points in 2D space
 * @param from - The starting point
 * @param to - The ending point
 * @returns The distance between the two points for each axis
 */
export const getOffset = (
  from: PointLike | undefined,
  to: PointLike,
): Point2D => {
  if (!from) return [0, 0];
  const [x1, y1] = toPoint2D(from);
  const [x2, y2] = toPoint2D(to);
  return [x2 - x1, y2 - y1];
};

/**
 * Move a point by an offset
 * @param point - The point to move
 * @param offset - The offset to move the point by
 * @returns Updated point or original point if offset is [0, 0]
 */
export const movePoint2D = (point: Point2D, offset: Point2D): Point2D => {
  const [x, y] = point;
  const [dx, dy] = offset;
  return !dx && !dy ? point : [x + dx, y + dy];
};

/**
 * Get the distance between two points in 2D space using the Pythagorean theorem
 * @param from - The starting point
 * @param to - The ending point
 * @returns The distance between the two points
 */
export const getDistance = (
  p1: PointLike | undefined,
  p2: PointLike,
): number =>
  !p1 ? Number.POSITIVE_INFINITY : Math.hypot(...getOffset(p1, p2));

/**
 * Get the metric distance [x, y] from a coordinate  to a base coordinate
 * @param coordinate
 * @param base
 * @returns
 */
export const convertCoordinateToMetricOffset = (
  coordinate: Coordinate,
  base: Coordinate,
): Point2D => {
  const { lat, lng } = coordinate;
  const distanceX = getCoordinateDistance(coordinate, { lat, lng: base.lng });
  const directionX = lng > base.lng ? 1 : -1;

  const distanceY = getCoordinateDistance(coordinate, { lat: base.lat, lng });
  const directionY = lat > base.lat ? 1 : -1;

  return [distanceX * directionX, distanceY * directionY];
};

/**
 * Convert a metric offset relative to a base coordinate back into a coordinate.
 * @param base - Origin coordinate used as (0,0)
 * @param offset - Metric offset expressed as [x, y] in meters
 * @returns Coordinate translated from the base by the provided offset
 */
export const convertMetricOffsetToCoordinate = (
  base: Coordinate,
  offset: Point2D,
): Coordinate => {
  const [x, y] = offset;
  const afterX = moveCoordinate(
    base,
    [Math.abs(x), 'meters'],
    [x >= 0 ? 90 : 270, 'degrees'],
  );
  return moveCoordinate(
    afterX,
    [Math.abs(y), 'meters'],
    [y >= 0 ? 180 : 0, 'degrees'],
  );
};

/**
 * Calculate the distance between two coordinates in meters.
 * @param coordinate1
 * @param coordinate2
 * @returns
 */
export function getCoordinateDistance(
  coordinate1: CoordinateLike,
  coordinate2: CoordinateLike,
) {
  // generally used geo measurement function
  const { lat: lat1, lng: lon1 } = toCoordinate(coordinate1);
  const { lat: lat2, lng: lon2 } = toCoordinate(coordinate2);
  const R = 6378.137; // Radius of earth in KM
  const dLat = (lat2 * Math.PI) / 180 - (lat1 * Math.PI) / 180;
  const dLon = (lon2 * Math.PI) / 180 - (lon1 * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d * 1000; // meters
}

/**
 * Get the closest coordinate to a target coordinate.
 * @param target
 * @param items
 * @returns
 */
export const getClosestCoordinate = <T extends Coordinate>(
  target: Coordinate,
  items: T[],
): T => {
  const first = items[0];
  if (!first) {
    throw new Error('No items to compare');
  }

  return items.reduce((closest, item) => {
    return getCoordinateDistance(target, item) <
      getCoordinateDistance(target, closest)
      ? item
      : closest;
  }, first);
};

const isFootprintCoordinates = (
  coord: Point2D[] | Coordinate[],
): coord is Coordinate[] => {
  const first = coord[0];
  return first ? 'lat' in first : false;
};

const MEASURE_POINTS = 100;

const getMeasurePoints = (line: Line, totalLength: number): number[] => {
  const lineLength = getDistance(line[0], line[1]);
  const lineRatio = lineLength / totalLength;
  const numberOfMeasurePoints = Math.round(lineRatio * MEASURE_POINTS);
  const measurePoints: number[] = [];

  if (numberOfMeasurePoints > 1) {
    for (let i = 0; i < 1; i += 1 / numberOfMeasurePoints) {
      measurePoints.push(i);
    }
  } else {
    measurePoints.push(0);
  }
  measurePoints.push(1);
  return measurePoints;
};

export const getBuildingWidth = (
  pointsOrCoordinates: Point2D[] | Coordinate[] = [],
): number => {
  if (pointsOrCoordinates.length === 0) {
    return 0;
  }

  const isFootprint = isFootprintCoordinates(pointsOrCoordinates);
  const base = isFootprint ? pointsOrCoordinates[0] : { lat: 0, lng: 0 };
  const points = (
    isFootprint && base
      ? pointsOrCoordinates.map((p) => convertCoordinateToMetricOffset(p, base))
      : pointsOrCoordinates
  ) as Point2D[];

  return cacheFactory(
    () => {
      const spans: number[] = [];

      const lines = points.reduce((acc, point, index) => {
        const nextPoint = points[(index + 1) % points.length];

        if (nextPoint) {
          acc.push([point, nextPoint]);
        }
        return acc;
      }, [] as Line[]);

      const totalLength = lines.reduce(
        (acc, line) => acc + getDistance(line[0], line[1]),
        0,
      );

      lines.forEach((line1, index1) => {
        const measurePoints = getMeasurePoints(line1, totalLength);
        const perpendicularLines = measurePoints.map((ratio) =>
          createPerpendicularLine(line1, 100000, ratio),
        );

        lines.forEach((line2, index2) => {
          if (index1 === index2) {
            return;
          }

          const intersectingLines: Line[] = perpendicularLines
            .map((perpendicularLine) => [
              findIntersectionPoint(perpendicularLine, line1),
              findIntersectionPoint(perpendicularLine, line2),
            ])
            .filter((line): line is Line => !!line[0] && !!line[1]);

          const distances = intersectingLines
            .map(([p1, p2]) => getDistance(p1, p2))
            .filter((distance) => distance > 0.005); // Filter out values like 7.290154618149881e-12 and 0

          if (distances.length) {
            spans.push(...distances);
          }
        });
      });

      // remove extreme values
      const filteredSpans = filterExtremeValues(sortBy(spans));

      // Return a value slighly lower than the median value
      return roundToDecimals(getMedianValue(filteredSpans, 0.4));
    },
    'getBuildingSpan',
    points.flat(),
  );
};

export const getSmallestPerpendicularSize = (
  points: Point2D[],
): [number, number] => {
  const [p1, p2, p3, p4] = points;

  if (!p1 || !p2 || !p3 || !p4) {
    return [0, 0];
  }

  const width = Math.min(
    getDistance(p1, p2),
    getDistance(p2, p3),
    getDistance(p3, p4),
    getDistance(p4, p1),
  );

  const height = Math.min(
    getDistanceToLine(p1, p2, p4),
    getDistanceToLine(p2, p3, p1),
    getDistanceToLine(p3, p4, p2),
    getDistanceToLine(p4, p1, p3),
  );

  return [width, height];
};

const getDistanceToLine = (p1: Point2D, p2: Point2D, p3: Point2D): number => {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  const [x3, y3] = p3;
  const numerator = Math.abs(
    (y2 - y1) * x3 - (x2 - x1) * y3 + x2 * y1 - y2 * x1,
  );
  const denominator = getDistance(p1, p2);
  return numerator / denominator;
};

const filterExtremeValues = (numbers: number[]): number[] => {
  const mean =
    numbers.reduce((sum, number) => sum + number, 0) / numbers.length;
  const variance =
    numbers.reduce((sum, number) => sum + (number - mean) ** 2, 0) /
    numbers.length;
  const standardDeviation = Math.sqrt(variance);
  const lowerBound = mean - 2 * standardDeviation;
  const upperBound = mean + 2 * standardDeviation;
  return numbers.filter(
    (number) => number >= lowerBound && number <= upperBound,
  );
};

export const findIntersectionPoint = (
  line1: Line,
  line2: Line,
): Point2D | undefined => {
  const [p1, p2] = line1;
  const [p3, p4] = line2;

  const x1 = p1[0];
  const y1 = p1[1];
  const x2 = p2[0];
  const y2 = p2[1];
  const x3 = p3[0];
  const y3 = p3[1];
  const x4 = p4[0];
  const y4 = p4[1];

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (denominator === 0) {
    // The lines are parallel
    return undefined;
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    // The intersection point is outside the line segments
    return undefined;
  }

  const x = x1 + ua * (x2 - x1);
  const y = y1 + ua * (y2 - y1);

  return [x, y];
};

const reverseLine = ([start, end]: Line): Line => [end, start];

const LAT_METERS_PER_DEGREE = 111_320;

// needs to be calculated for each longitude
const getLNGMetersPerDegree = (longitude: number) =>
  LAT_METERS_PER_DEGREE * Math.cos((longitude * Math.PI) / 180); // math be mathing

/**
 * Convert meters to degrees.
 * @param meters
 * @param axis 'lat' or 'lng'
 * @returns degrees
 */
const precisionMetersToDegrees = (
  meters: number,
  axis: 'lat' | ['lng', longitude: number],
): number => {
  if (axis === 'lat') return meters / LAT_METERS_PER_DEGREE;
  const [, longitude] = axis;
  return meters / getLNGMetersPerDegree(longitude);
};

/**
 * Check if coordinates are the same (within reasonable precision)
 * @param a - first coordinate
 * @param b - second coordinate
 * @param precisionMeters - precision in meters (default 50 meters)
 * @returns true if coordinates are the same (within precision)
 */
export const coordinatesEqual = (
  a?: Coordinate,
  b?: Coordinate,
  precisionMeters = 50,
): boolean => {
  if (!a || !b) return false;
  const latPrecision = precisionMetersToDegrees(precisionMeters, 'lat');

  const averageLng = (a.lng + b.lng) / 2;
  const lngPrecision = precisionMetersToDegrees(precisionMeters, [
    'lng',
    averageLng,
  ]);

  return (
    Math.abs(a.lat - b.lat) < latPrecision &&
    Math.abs(a.lng - b.lng) < lngPrecision
  );
};

type AngleWithUnit = [angle: number, unit: 'degrees' | 'radians'];
const angleToRadians = (angle: AngleWithUnit) => {
  const [angleValue, angleUnit] = angle;
  switch (angleUnit) {
    case 'degrees':
      return angleValue * (Math.PI / 180);
    case 'radians':
      return angleValue;
  }
};

type DistanceWithUnit = [distance: number, unit: 'meters' | 'degrees'];
const getCoordinateDistanceDegrees = (
  distance: DistanceWithUnit,
  originLatitude: number,
) => {
  const [distanceValue, distanceUnit] = distance;
  switch (distanceUnit) {
    case 'meters':
      return {
        lat: distanceValue / LAT_METERS_PER_DEGREE,
        lng: distanceValue / getLNGMetersPerDegree(originLatitude),
      };
    case 'degrees':
      return {
        lat: distanceValue,
        lng: distanceValue,
      };
  }
};
export const moveCoordinate = (
  /** Starting coordinate */
  coord: Coordinate,
  /** Distance to move */
  distance: [distance: number, unit: 'meters' | 'degrees'],
  /** Geographic bearing [value & unit] where 0° = North, 90° = East, 180° = South, 270° = West */
  angle: AngleWithUnit,
) => {
  const { lat, lng } = coord;
  const radians = angleToRadians(angle);
  const coordDistances = getCoordinateDistanceDegrees(distance, lat);

  // Convert from geographic bearing (0° = North) to mathematical coordinates (0° = East)
  const mathRadians = Math.PI / 2 - radians;

  return {
    lat: lat + coordDistances.lat * Math.sin(mathRadians),
    lng: lng + coordDistances.lng * Math.cos(mathRadians),
  };
};

export const coordinateToTuple = ({
  lng,
  lat,
}: Coordinate): CoordinateTuple => [lng, lat];

/**
 * Some drawing libraries require a closed polygon (last point is the same as the first point).
 * Running this function will ensure that the polygon is closed.
 * @param coordinates
 * @returns
 */
export const closeCoordinatesPolygon = (
  coordinates: Coordinate[],
): Coordinate[] => {
  // Close ring if needed
  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];
  if (first && last && !coordinatesEqual(first, last, 0.01)) {
    return [...coordinates, { ...first }];
  }
  return coordinates;
};

export const isCoordinate = (
  maybeCoordinate: unknown,
): maybeCoordinate is Coordinate =>
  guards.is.object(maybeCoordinate) &&
  guards.is.number(maybeCoordinate.lat) &&
  guards.is.number(maybeCoordinate.lng);

/**
 * Convert a coordinate-like object to a coordinate object
 * @param e - The coordinate-like object to convert
 * @returns The coordinate object
 */
export const toCoordinate = (e: CoordinateLike): Coordinate => {
  if (isCoordinate(e)) return e;
  if (e instanceof LngLat) return { lng: e.lng, lat: e.lat };
  const [lng, lat] = e;
  return { lng, lat };
};
